/**
 * ★ DEV ONLY: the address a published image is fetched from when the build runs against the stack.
 *
 * Every image URL the API returns is production's tokenless download form,
 * `https://firebasestorage.googleapis.com/v0/b/{bucket}/o/{encoded path}?alt=media` - ONE shape for
 * an avatar and an article rendition alike (`publicObjectUrl`,
 * src/modules/authors/services/storage-paths.ts, held character for character to
 * `renditionUrl`, src/modules/articles/services/article-public.presenter.ts). Nothing on a
 * laptop serves that host: the local stack writes its renditions into the Storage EMULATOR, and the
 * acceptance suite says the same of itself (test/acceptance/pictures-a5.spec.ts).
 *
 * THE EMULATOR ANSWERS THE SAME PATH. When the SDK reads an object's bytes it asks for
 * `{protocol}://{host}/v0/b/{bucket}/o/{encoded path}?alt=media` (`makeUrl` and `getBytes$1` in
 * @firebase/storage 0.14.5's browser build, dist/index.esm.js, its lines 571-577 and 1730-1732),
 * and connecting the emulator changes nothing but the host, the port and the protocol (its lines
 * 3196-3204). So an object's emulator address is its production address with the origin swapped,
 * and that is the whole mapping: bucket, path, encoding and query are carried over untouched.
 *
 * ONLY WHEN THE BUILD NAMES THE EMULATOR - `VITE_FIREBASE_STORAGE_EMULATOR_URL`, read in
 * ./storage-config.ts - which only `dev:stack` and `dev:tunnel` do (the tunnel's is the page's own
 * origin, which the dev server forwards). Every other build, production included, gets
 * each URL back exactly as it came, and so does anything that is not a production Storage URL:
 * another host, another scheme, another path, or a string that is not a URL at all.
 *
 * APPLIED WHERE A RETURNED URL BECOMES A `src`: the editor's cover and image blocks
 * (routes/articles/EditorRoute.tsx, editor/BlockList.tsx) and the applicant's avatar (AvatarField).
 */
import { useState } from 'react';
import type { SyntheticEvent } from 'react';
import { readStorageEmulator } from './storage-config';

/** Production's download origin, `PUBLIC_OBJECT_URL_HOST` in storage-paths.ts. */
const PRODUCTION_ORIGIN = 'https://firebasestorage.googleapis.com';

/** The origin, the download API's version and the bucket segment: how an object URL begins. */
const PRODUCTION_OBJECT_PREFIX = `${PRODUCTION_ORIGIN}/v0/b/`;

function configuredEmulatorOrigin(): string | null {
  try {
    return readStorageEmulator()?.origin ?? null;
  } catch {
    // A value the uploader refuses is not one to rewrite a picture onto, and a picture must never
    // throw while a screen renders: the URL is left as the API sent it.
    return null;
  }
}

/**
 * `url` where the local stack serves it, or `url` itself. `emulatorOrigin` is the build's unless a
 * caller - a spec - passes its own.
 */
export function emulatorImageUrl(
  url: string,
  emulatorOrigin: string | null = configuredEmulatorOrigin(),
): string {
  if (emulatorOrigin === null || !url.startsWith(PRODUCTION_OBJECT_PREFIX)) {
    return url;
  }
  return `${emulatorOrigin}${url.slice(PRODUCTION_ORIGIN.length)}`;
}

/**
 * ★ DEV ONLY, AND ONLY WHEN THE EMULATOR DOES NOT HOLD THE PICTURE: back to the address the API gave.
 *
 * The emulator holds what the local stack wrote - an upload, its renditions. It never holds an
 * old-app picture: the import catalogues those where they already live, in the real bucket, and
 * nothing copies them to a laptop (`image-rows.ts`, "files stay where they are"). The paths cannot
 * tell the two apart - the platform writes its renditions under the old apps' own prefixes, so the
 * URLs every shipped client holds never change - so the emulator is asked first and a picture it
 * does not have is fetched from the API's address, as a reader of the old apps fetches it (the live
 * rules allow unauthenticated reads there; article-public.presenter.ts, `renditionUrl`). The owner
 * on real cabinets, 2026-10-05: "what about covers? it will work or i still will see a lsit of
 * articles without iamges?" - on the live server they always did; this is the laptop catching up.
 *
 * For an `<img>`: an `onError` that swaps the element's `src` to `url` once - React leaves an
 * attribute it did not change alone, and a second failure (the real file gone too) stops there.
 * `undefined` where nothing was rewritten: production builds attach no handler at all.
 */
export function servedUrlOnError(
  url: string,
  emulatorOrigin: string | null = configuredEmulatorOrigin(),
): ((event: SyntheticEvent<HTMLImageElement>) => void) | undefined {
  if (emulatorImageUrl(url, emulatorOrigin) === url) {
    return undefined;
  }
  return (event) => {
    const image = event.currentTarget;
    if (image.getAttribute('src') !== url) {
      image.setAttribute('src', url);
    }
  };
}

/**
 * The same for a component that keeps its own failure state - Mantine's `Avatar` swaps a failed
 * image for its initials, so the element is gone before an `onError` could swap its `src`. The
 * picture's `src` and the `onError` to hand it (`imageProps`): after the emulator fails, the API's
 * address, once.
 */
export function useServedImageSrc(
  url: string | null,
  emulatorOrigin: string | null = configuredEmulatorOrigin(),
): { readonly src: string | null; readonly onError: () => void } {
  const [failed, setFailed] = useState<string | null>(null);
  const local = url === null ? null : emulatorImageUrl(url, emulatorOrigin);
  return {
    src: url !== null && failed === url ? url : local,
    onError: () => {
      if (url !== null && local !== url) {
        setFailed(url);
      }
    },
  };
}
